import { type Accessor, createSignal } from "solid-js";

/**
 * §5.2: セッションのライフサイクルを state machine 化する。 home / history / session の全 feature から
 * `requestSessionStart` 経由で参照される cross-feature な調整状態なので、 feature には属さず
 * `state/` (shared) に置く。 実行中セッションの蓄積状態 (notification 配列 / marPoints / 永続化
 * record の組み立て等) は `features/session/state/session-store.ts` 側。
 *
 * 遷移グラフ (mermaid) と開始入口の 2 層モデルの canonical は docs/session-lifecycle.md。
 *
 * 各 state での意味と resource lifecycle:
 * - `idle`: 開始前 / 完了後の安息状態。 worker / pipController は不在
 * - `starting`: 開始ボタン直後 / 自動セッション開始 (autoStart) のリソース確保中。 user activation を
 *   保つため click handler で sync transition、 microtask 内で resource acquire を駆動する
 * - `running`: detection ループが回る通常状態
 * - `completed`: handleStop 経由の完了。 1 秒後に resetSession() で `idle` に戻る
 */
export type SessionLifecycle = "idle" | "starting" | "running" | "completed";

const [lifecycle, setLifecycle] = createSignal<SessionLifecycle>("idle");

export const sessionLifecycle: Accessor<SessionLifecycle> = lifecycle;

/** 直接 transition (running / completed / idle 等)。 session-store の startSession / completeSession /
 * resetSession が蓄積状態のリセットと並べて呼ぶ。 */
export function setSessionLifecycle(next: SessionLifecycle): void {
  setLifecycle(next);
}

/**
 * §5.2: セッション開始の **唯一の入口** (冪等)。 PiP 開閉 / カメラ権限に関わらず、 単に「セッションを
 * 開始する」 という意図だけを表明する。 state を 'starting' に切替え、 呼び出し側は続けて
 * `navigate("/session")` する。 'starting' state により session-page の <Switch> が active branch に
 * 切替わり、 SessionPageActive.onMount が resource acquire を起動する (= autoStart の駆動は route
 * state ではなく lifecycle 'starting' が担う、 issue #101)。
 *
 * calibration UI 廃止 PR で `withCalibration` option は撤去 (= キャリブレーション中の 'calibrating'
 * state も同 PR で union から削除済)。
 */
export function requestSessionStart(): void {
  setLifecycle("starting");
}
